import { Box, Divider, Grid, InputBase, Paper, Typography } from '@mui/material'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectLimitOrderPremium, selectOrderType, selectStrategyBuilderMode, selectTradeOrders } from '../../../selectors/strategyBuilder'
import { Broker_OrderType, Order, OrderStatus, TransactionType } from '../../../proto/generated/order'
import { TradeOrder, updateLimitOrderPremium, updateTradeOrders } from '../../../reducer/strategyBuilder.reducer'
import { ExtraSmallButton } from '../../custom'
import { selectOrdersHistory } from '../../../selectors/orders'
import ThinArrowDownIcon from '../../../assets/icons/ThinArrowDownIcon'
import ThinArrowUpIcon from '../../../assets/icons/ThinArrowUpIcon'
import { calculateGcd, getRoundedPrice } from '../../../util/orders'
import useSelectPrimaryOrUnderlyingSecurity from '../../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import { debounce } from 'throttle-debounce'
import { getOrderBook, getSecuritySnapshot } from '../../../selectors/security'
import { getSecurityKey } from '../../../util/security'
import { limitPriceFromOrderBookOrLtp } from '../../../util/orderBookPrice'

export type TradeOrderPremiumProps = {
    disabled?: boolean
}

const TradeOrderPremium: React.FC<TradeOrderPremiumProps> = ({disabled}) => {
    const dispatch = useDispatch()
    const strategyBuilderMode = useSelector(selectStrategyBuilderMode);
    const { primaryOrUnderlying: mainSecurity } = useSelectPrimaryOrUnderlyingSecurity();
    const orderType = useSelector(selectOrderType)
    const tradeOrders = useSelector(selectTradeOrders);
    const placedOrders = useSelector(selectOrdersHistory);
    const limitOrderPremium = useSelector(selectLimitOrderPremium);
    const [premiumInput, setPremiumInput] = useState(limitOrderPremium);

    const previousLimitValue = useRef(limitOrderPremium);

    useEffect(() => {
        previousLimitValue.current !== limitOrderPremium && setPremiumInput(limitOrderPremium);
        previousLimitValue.current = limitOrderPremium;
    }, [limitOrderPremium])

    const orderIdToOrderMap = useMemo(() => {
        if (!placedOrders) return {} as Record<string, Order>
        return placedOrders.reduce(
            (acc, o) => {
                acc[o.orderId] = o
                return acc
            },
            {} as Record<string, Order>
        )
    }, [placedOrders])

    const elligibleTradeOrders = useMemo(() => {
            if (strategyBuilderMode === 'trade') return tradeOrders.filter((o) => o.selected)

            return tradeOrders.filter((o) => {
                return (!o.referenceOrderId && o.selected) || (o.referenceOrderId && orderIdToOrderMap[o.referenceOrderId]?.status !== OrderStatus.EXECUTED)
            })
        }, [tradeOrders, strategyBuilderMode, orderIdToOrderMap])

    const gcd = useMemo(() => calculateGcd(elligibleTradeOrders.map(o => o.quantity)), [elligibleTradeOrders]);

    const eligibleOrdersPremiumKey = useMemo(
        () => elligibleTradeOrders
            .map((o) => `${o.clientOrderId}:${o.price}:${o.quantity}:${o.transactionType}`)
            .join('|'),
        [elligibleTradeOrders]
    )

    // One-time snapshot for market legs missing a reference price (e.g. legacy price: 0 rows)
    useEffect(() => {
        if (orderType !== Broker_OrderType.MARKET_ORDER) return

        let changed = false
        const updated = tradeOrders.map((t) => {
            if (t.price) return t
            const securityKey = getSecurityKey(t.security.exchange, t.security.code)
            const price = limitPriceFromOrderBookOrLtp(
                getOrderBook(securityKey),
                t.transactionType,
                getSecuritySnapshot(securityKey)?.lastTradedPrice
            )
            if (!price) return t
            changed = true
            return { ...t, price }
        })
        if (changed) dispatch(updateTradeOrders(updated))
    }, [orderType, tradeOrders, dispatch])

    useEffect(() => {
        if (!elligibleTradeOrders.length) {
            dispatch(updateLimitOrderPremium(0));
            return;
        }

        const totalPremium = elligibleTradeOrders.reduce((acc, order) => {
            const orderPrice = order.price ?? 0;
            if (order.transactionType === TransactionType.BUY) {
                return acc - orderPrice * order.quantity;
            }
            return acc + orderPrice * order.quantity;
        }, 0);

        dispatch(updateLimitOrderPremium(totalPremium))
    }, [eligibleOrdersPremiumKey, elligibleTradeOrders, dispatch])

    const redistributePremium = useCallback(debounce(200, (val: number) => {
        if (!elligibleTradeOrders.length) return;

        const currentPremium = limitOrderPremium;
        const premiumChange = val - currentPremium;
        
        if (Math.abs(premiumChange) < 0.01) return; // No significant change

        // Calculate total value for proportional distribution across all orders
        const totalOrderValue = elligibleTradeOrders.reduce((acc, order) => {
            return acc + (order.price * order.quantity);
        }, 0);

        if (totalOrderValue === 0) return;

        const newTradeOrders = elligibleTradeOrders.map((order) => {
            // Calculate this order's weight in the total portfolio
            const orderWeight = (order.price * order.quantity) / totalOrderValue;
            
            // Calculate the adjustment needed for this order
            let priceAdjustment = 0;
            
            if (order.transactionType === TransactionType.BUY) {
                // For BUY orders: premium increase means buy prices should decrease
                priceAdjustment = -(premiumChange * orderWeight) / order.quantity;
            } else {
                // For SELL orders: premium increase means sell prices should increase
                priceAdjustment = (premiumChange * orderWeight) / order.quantity;
            }

            const newPrice = getRoundedPrice(
                Math.max(0.05, order.price + priceAdjustment), // Minimum price of 0.05
                mainSecurity?.tickSize || 5
            );

            return {
                ...order,
                price: newPrice
            };
        });

        // Calculate actual premium after adjustments
        let actualPremium = 0;
        newTradeOrders.forEach(order => {
            if (order.transactionType === TransactionType.BUY) {
                actualPremium -= order.price * order.quantity;
            } else {
                actualPremium += order.price * order.quantity;
            }
        });

        // Fine-tune adjustment if there's still a difference
        const remainingDifference = val - actualPremium;
        if (Math.abs(remainingDifference) > 0.01) {
            // Find the order with largest value to make final adjustment
            let largestOrderIndex = 0;
            let largestOrderValue = 0;
            
            newTradeOrders.forEach((order, index) => {
                const orderValue = order.price * order.quantity;
                if (orderValue > largestOrderValue) {
                    largestOrderValue = orderValue;
                    largestOrderIndex = index;
                }
            });

            const largestOrder = newTradeOrders[largestOrderIndex];
            let finalAdjustment = remainingDifference / largestOrder.quantity;
            
            // Apply adjustment in the correct direction based on transaction type
            if (largestOrder.transactionType === TransactionType.BUY) {
                finalAdjustment = -finalAdjustment; // Inverse for buy orders
            }
            
            newTradeOrders[largestOrderIndex] = {
                ...largestOrder,
                price: getRoundedPrice(
                    Math.max(0.05, largestOrder.price + finalAdjustment), 
                    mainSecurity?.tickSize || 5
                )
            };
        }
        previousLimitValue.current = val;

        dispatch(updateTradeOrders(newTradeOrders));
        dispatch(updateLimitOrderPremium(val));
    }), [elligibleTradeOrders, mainSecurity?.tickSize, limitOrderPremium])

    const onPremiumInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const multiplier = mainSecurity?.priceMultiplier ?? 100
        const val = (event.target.value === '' ? 0 : Number(event.target.value)) * multiplier*gcd;
        setPremiumInput(val);
        redistributePremium(val);
    }, [elligibleTradeOrders, redistributePremium, gcd, mainSecurity])

    const onIncreaseLimitPrice = useCallback(() => {
        const newLimitPrice = limitOrderPremium + (mainSecurity?.priceMultiplier??100) * gcd;
        setPremiumInput(newLimitPrice);
        redistributePremium(newLimitPrice);
    }, [limitOrderPremium, redistributePremium, gcd, mainSecurity]);

    const onDecreaseLimitPrice = useCallback(() => {
        const newLimitPrice = limitOrderPremium - (mainSecurity?.priceMultiplier??100) * gcd;
        setPremiumInput(newLimitPrice);
        redistributePremium(newLimitPrice);
    }, [limitOrderPremium, redistributePremium, gcd, mainSecurity]);

    return (
        <Grid container gap={1}>
            <Grid container item xs={12}>
                <Grid container justifyContent={'space-between'} alignItems={'baseline'} item xs={12} sx={{ maxHeight: '20px' }} >
                    <Typography variant={'caption'} sx={{ color: (theme) => theme.palette.text.secondary }}>NET PREMIUM</Typography>
                    <Typography variant="caption">
                        <span
                            className="trade-panel-text">{`${limitOrderPremium > 0 ? 'GET ' : limitOrderPremium < 0 ? 'PAY ' : ''}`}</span>
                        <span
                            className={limitOrderPremium > 0 ? 'premium-text red-text' : limitOrderPremium < 0 ? 'premium-text green-text' : ''}>{Math.round(Math.abs(limitOrderPremium/(mainSecurity?.priceMultiplier || 100)))?.toLocaleString('en-IN', { style: 'decimal' })}</span>
                            </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Box
                        sx={{
                            backgroundColor: (theme) => theme.palette.background.default
                        }}
                        style={{
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            borderRadius: '2px'
                        }}
                    >
                        <InputBase
                            inputProps={{
                                style: { textAlign: 'right' }
                            }} sx={{ width: '100%', mr: 1 }}
                            size={'small'}
                            disabled={orderType !== Broker_OrderType.LIMIT_ORDER || disabled}
                            value={!isNaN(gcd) && gcd !== 0 ? Math.abs(Number(premiumInput/gcd/(mainSecurity?.priceMultiplier || 100))).toFixed(2) : '0.00' }
                            onChange={onPremiumInputChange} />
                        <Paper className="flex !rounded-sm relative items-center"
                            sx={{ background: (theme) => theme.palette.background.default }}>
                            <ExtraSmallButton data-event={'Net Premium Down Clicked'}
                                data-event-source={'Trade'}
                                style={{ width: '40px', height: '100%' }}
                                onClick={onDecreaseLimitPrice}
                                disabled={orderType !== Broker_OrderType.LIMIT_ORDER || disabled}
                                className="button-icons" size={'small'}
                                color={'inherit'}><ThinArrowDownIcon
                                    className="trade-panel-global-edit-icons" /></ExtraSmallButton>
                            <Divider orientation="vertical" variant="middle" flexItem />
                            <ExtraSmallButton data-event={'Net Premium Up Clicked'}
                                data-event-source={'Trade'}
                                style={{ width: '40px', height: '100%' }}
                                onClick={onIncreaseLimitPrice}
                                disabled={orderType !== Broker_OrderType.LIMIT_ORDER || disabled}
                                className="button-icons" size={'small'}
                                color={'inherit'}><ThinArrowUpIcon
                                    className="trade-panel-global-edit-icons" /></ExtraSmallButton>
                        </Paper>
                    </Box>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default memo(TradeOrderPremium)